import { useState } from "react";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { CatalogSearch, type CatalogHit } from "@/components/wholesale/catalog-search";
import {
  addCatalogItem, discountForStore, editorItemKey, lineTotalOf, patchItem, removeItem, setCatalogItemQuantity, unitPriceOf,
  type EditorItem,
} from "@/lib/wholesale-items";
import { fmtKm, fmtPct, parseNumberInput } from "@/lib/wholesale-format";
import { cn } from "@/lib/utils";

export function OrderItemsEditor({
  items,
  partnerDiscount,
  storeDiscounts,
  onChange,
  readOnly = false,
  framed = true,
}: {
  items: EditorItem[];
  partnerDiscount: number;
  storeDiscounts?: { storeId: string; discountPct: number }[];
  onChange: (items: EditorItem[]) => void;
  readOnly?: boolean;
  /** `false` kad je uređivač već unutar kartice koja crta okvir. */
  framed?: boolean;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  // Sirovi tekst polja živi odvojeno od brojeva da unos "12," ili prazna količina
  // ne budu odmah pregaženi formatiranom vrijednošću.
  const [raw, setRaw] = useState<Record<string, string>>({});

  const setRawField = (key: string, value: string) => setRaw((r) => ({ ...r, [key]: value }));
  const dropRaw = (key: string) => setRaw((r) => { const { [key]: _drop, ...rest } = r; return rest; });

  // Panel ostaje otvoren nakon dodavanja — u jednom prolazu se slaže više artikala,
  // a stepper u svakom redu pretrage odmah mijenja količinu u narudžbi.
  function handlePick(hit: CatalogHit) {
    const pct = discountForStore(hit.storeId, partnerDiscount, storeDiscounts);
    onChange(addCatalogItem(items, hit, pct));
  }
  function setPickedQuantity(hit: CatalogHit, quantity: number) {
    const pct = discountForStore(hit.storeId, partnerDiscount, storeDiscounts);
    onChange(setCatalogItemQuantity(items, hit, pct, quantity));
    dropRaw(`q:${editorItemKey(hit)}`);
  }
  const quantityInOrder = (hit: CatalogHit) =>
    items.find((i) => editorItemKey(i) === editorItemKey(hit))?.quantity ?? 0;
  const pieceCount = items.reduce((sum, i) => sum + (i.quantity || 0), 0);

  function setQty(key: string, text: string) {
    setRawField(`q:${key}`, text);
    const n = parseNumberInput(text);
    onChange(patchItem(items, key, { quantity: n === null ? 0 : Math.max(0, Math.round(n)) }));
  }
  function blurQty(it: EditorItem, key: string) {
    dropRaw(`q:${key}`);
    if (!it.quantity || it.quantity < 1) onChange(patchItem(items, key, { quantity: 1 }));
  }
  function setPrice(key: string, text: string) {
    setRawField(`p:${key}`, text);
    const n = parseNumberInput(text);
    // Cijena ne može biti negativna — server bi takav override odbio, pa se
    // odbija odmah ovdje da prikaz odgovara onome što će se spremiti.
    onChange(patchItem(items, key, { overridePrice: n === null ? null : Math.max(0, n) }));
  }

  const rows = items.map((it) => {
    const key = editorItemKey(it);
    return {
      it,
      key,
      qtyText: raw[`q:${key}`] ?? String(it.quantity ?? ""),
      priceText: raw[`p:${key}`] ?? (it.overridePrice === null ? "" : String(it.overridePrice)),
      basePrice: fmtKm(unitPriceOf({ ...it, overridePrice: null })),
    };
  });

  const thumb = (it: EditorItem, size: string) =>
    it.imageUrl
      ? <img src={it.imageUrl} alt="" className={cn(size, "object-cover shrink-0 ring-1 ring-foreground/10")} loading="lazy" />
      : <div className={cn(size, "bg-muted shrink-0")} />;

  const empty = (
    <div className="p-8 text-center text-xs text-muted-foreground">
      Narudžba još nema stavki.{!readOnly && " Dodaj prvi artikal iz kataloga."}
    </div>
  );

  return (
    <div className={cn("flex flex-col", framed && "ring-1 ring-foreground/10")}>
      {/* Desktop: tabela */}
      <div className="hidden md:block">
        {/* Fiksni raspored: kolone drže širinu, a dugi naziv se skraćuje umjesto da tabela izađe iz okvira. */}
        <table className="w-full table-fixed text-xs">
          <thead>
            <tr className="border-b bg-muted/50 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
              <th className="py-2 px-3 text-left font-medium">Artikal</th>
              <th className="py-2 px-2 text-right font-medium w-20">Količina</th>
              <th className="py-2 px-2 text-right font-medium w-36">Cijena</th>
              <th className="py-2 px-3 text-right font-medium w-28">Iznos</th>
              {!readOnly && <th className="w-9" />}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ it, key, qtyText, priceText, basePrice }) => (
              <tr key={key} className="border-b last:border-b-0 align-middle">
                <td className="py-2 px-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    {thumb(it, "size-9")}
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium line-clamp-2 leading-snug break-words" title={it.name}>{it.name || "Bez naziva"}</span>
                      <span className="text-muted-foreground tabular-nums truncate flex items-center gap-1.5">
                        {it.sku || "bez SKU"}
                        <span className="text-foreground/20">·</span>
                        <span>{it.storeName}</span>
                      </span>
                    </div>
                    {it.unavailable && (
                      <span className="inline-flex items-center gap-1 text-red-600 shrink-0">
                        <AlertTriangle className="h-3 w-3" />
                        Nedostupno
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-2 px-2 text-right">
                  {readOnly ? (
                    <span className="tabular-nums">{it.quantity}</span>
                  ) : (
                    <Input
                      type="number" min={1} step={1} inputMode="numeric"
                      aria-label={`Količina, ${it.name}`}
                      className="h-7 w-16 ml-auto text-xs text-right tabular-nums"
                      value={qtyText}
                      onChange={(e) => setQty(key, e.target.value)}
                      onBlur={() => blurQty(it, key)}
                    />
                  )}
                </td>
                <td className="py-2 px-2 text-right">
                  <div className="flex flex-col items-end gap-0.5">
                    {readOnly ? (
                      <span className={cn("tabular-nums", it.overridePrice !== null && "font-medium")}>{fmtKm(unitPriceOf(it))}</span>
                    ) : (
                      <Input
                        inputMode="decimal"
                        aria-label={`Cijena, ${it.name}`}
                        className="h-7 w-28 text-xs text-right tabular-nums"
                        placeholder={basePrice}
                        value={priceText}
                        onChange={(e) => setPrice(key, e.target.value)}
                        onBlur={() => dropRaw(`p:${key}`)}
                      />
                    )}
                    <span className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap" title="Cjenovnik · rabat">
                      {fmtKm(it.listPrice)} · {fmtPct(it.discountPct)}
                    </span>
                  </div>
                </td>
                <td className="py-2 px-3 text-right tabular-nums font-medium">{fmtKm(lineTotalOf(it))}</td>
                {!readOnly && (
                  <td className="py-2 pr-1 text-right">
                    <Button variant="ghost" size="icon-xs" aria-label="Ukloni stavku" onClick={() => onChange(removeItem(items, key))}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && empty}
      </div>

      {/* Telefon: kartice */}
      <div className="md:hidden divide-y divide-foreground/10">
        {rows.map(({ it, key, qtyText, priceText, basePrice }) => (
          <div key={key} className="p-3 flex flex-col gap-2.5 text-xs">
            <div className="flex items-start gap-2.5 min-w-0">
              {thumb(it, "size-11")}
              <div className="flex flex-col min-w-0 flex-1">
                <span className="font-medium leading-snug">{it.name || "Bez naziva"}</span>
                <span className="text-muted-foreground tabular-nums flex items-center gap-1.5 flex-wrap">
                  {it.sku || "bez SKU"}
                  <span className="text-foreground/20">·</span>
                  {it.storeName}
                </span>
                {it.unavailable && (
                  <span className="inline-flex items-center gap-1 text-red-600 mt-0.5">
                    <AlertTriangle className="h-3 w-3" />
                    Nedostupno
                  </span>
                )}
              </div>
              {!readOnly && (
                <Button variant="ghost" size="icon-xs" className="-mr-1 -mt-1" aria-label="Ukloni stavku" onClick={() => onChange(removeItem(items, key))}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
              <div className="flex flex-col gap-1">
                <Label htmlFor={`qty-${key}`} className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Količina</Label>
                {readOnly ? (
                  <span className="h-8 flex items-center tabular-nums">{it.quantity}</span>
                ) : (
                  <Input
                    id={`qty-${key}`} type="number" min={1} step={1} inputMode="numeric"
                    className="h-8 text-xs tabular-nums"
                    value={qtyText}
                    onChange={(e) => setQty(key, e.target.value)}
                    onBlur={() => blurQty(it, key)}
                  />
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor={`price-${key}`} className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Cijena</Label>
                {readOnly ? (
                  <span className={cn("h-8 flex items-center tabular-nums", it.overridePrice !== null && "font-medium")}>{fmtKm(unitPriceOf(it))}</span>
                ) : (
                  <Input
                    id={`price-${key}`} inputMode="decimal"
                    className="h-8 text-xs tabular-nums"
                    placeholder={basePrice}
                    value={priceText}
                    onChange={(e) => setPrice(key, e.target.value)}
                    onBlur={() => dropRaw(`p:${key}`)}
                  />
                )}
              </div>
              <div className="flex flex-col gap-1 items-end min-w-[5.5rem]">
                <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Iznos</span>
                <span className="h-8 flex items-center tabular-nums font-medium text-sm">{fmtKm(lineTotalOf(it))}</span>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground tabular-nums">
              Lista {fmtKm(it.listPrice)} · rabat {fmtPct(it.discountPct)}
            </p>
          </div>
        ))}
        {items.length === 0 && empty}
      </div>

      {!readOnly && (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="flex items-center justify-center gap-1.5 w-full py-2.5 text-xs font-medium border-t border-dashed border-foreground/20 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Dodaj artikal
        </button>
      )}

      <Sheet open={pickerOpen} onOpenChange={setPickerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col gap-3 p-4">
          <SheetHeader className="p-0">
            <SheetTitle className="text-sm">Dodaj artikal</SheetTitle>
            <SheetDescription className="text-xs">
              Pretraga ide po shopovima uključenim u veleprodaju.
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 min-h-0 flex flex-col">
            {pickerOpen && (
              <CatalogSearch onPick={handlePick} quantityOf={quantityInOrder} onSetQuantity={setPickedQuantity} />
            )}
          </div>
          <SheetFooter className="p-0 pt-3 border-t flex-row items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground tabular-nums">
              U narudžbi: {items.length} {items.length === 1 ? "artikal" : "artikala"} · {pieceCount} kom
            </span>
            <Button size="sm" onClick={() => setPickerOpen(false)}>Gotovo</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
